import { ChangeEvent, ReactNode, SyntheticEvent, useContext, useEffect, useRef, useState } from "react"
import { InputText } from "primereact/inputtext"
import { Toast } from "primereact/toast"
import FieldError, { FormErrors } from "../../FieldError"
import { GetUserResponseData, LabelFormat, LoadingDefaultAddress, UpdateUserPayload } from "../../../api/types/user"
import { validateUser } from "./validate"
import { classNames } from "primereact/utils"
import commonStyles from "../../common.module.css"
import { Dropdown, DropdownChangeParams, DropdownProps } from "primereact/dropdown"
import { useUserDetail } from "../../../api/queries/useUserDetail"
import { Button } from "primereact/button"
import { useUpdateUser } from "../../../api/queries/useUpdateUser"
import userContext from "../../../context/user"
import { useAddresses } from "../../../api/queries/useAddresses"
import { Address, AddressSimple } from "../../../api/types/address"
import styles from "../styles.module.css"
import AddressBook from "../../AddressBook"
import { useConstants } from "../../../api/queries/useConstants"
import { PhoneInput } from "../../common/PhoneInput"
import { splitNumber } from "../../common/PhoneInput/utils"
import { formatAddress } from "../../../common/formatAddress"
import { useNavigate } from "react-router-dom"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

export default function UserProfile() {
  const { user, setUser } = useContext(userContext)
  const updateUser = useUpdateUser(user?.uuid)
  const { data: userDetail } = useUserDetail(user?.uuid)
  const { data: addresses } = useAddresses({ limit: 0, offset: 0 })
  const { data: constants } = useConstants()
  const navigate = useNavigate()

  const labelFormatOptions = [
    { value: "A4", label: "A4 - 4 štítky na stránku" },
    { value: "A6", label: "A6 - 1 štítek na stránku" }
  ]

  const [firstname, setFirstname] = useState<string>("")
  const handleFirstnameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setFirstname(event.target.value)
  }

  const [lastname, setLastname] = useState<string>("")
  const handleLastnameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLastname(event.target.value)
  }

  const [email, setEmail] = useState<string>("")
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value)
  }

  const [billingEmail, setBillingEmail] = useState<string>("")
  const handleBillingEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setBillingEmail(event.target.value)
  }

  const [phonePrefix, setPhonePrefix] = useState<string>("")
  const [phone, setPhone] = useState<number | null>(null)
  const handlePhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setPhonePrefix(prefix)
    setPhone(value)
    resetErrorsFor(["phone", "mobile"])
  }

  const [mobilePrefix, setMobilePrefix] = useState<string>("")
  const [mobile, setMobile] = useState<number | null>(null)
  const handleMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setMobilePrefix(prefix)
    setMobile(value)
    resetErrorsFor(["mobile", "phone"])
  }

  const [billingAddress, setBillingAdress] = useState<string>("")
  const handleBillingAddressChange = (event: DropdownChangeParams): void => {
    setBillingAdress(event.value)
  }

  const [loadingDefaultAddress, setLoadingDefaultAddress] = useState<LoadingDefaultAddress | null>(null)
  const handleLoadingDefaultAddressChange = (event: DropdownChangeParams): void => {
    const address = addresses?.results.addressList.find((address) => address.uuid === event.value)

    if (address) {
      setLoadingDefaultAddress(Object.assign({}, loadingDefaultAddress, {
        city: address.city,
        country: address.country,
        name: address.name,
        street: address.street,
        streetDetails: address.streetDetails,
        zip: address.zip,
      }))
    }
  }

  const handleRemoveLoadingDefaultAddress = (): void => {
    setLoadingDefaultAddress(null)
  }

  const [labelFormat, setLabelFormat] = useState<LabelFormat>("A6")
  const handleLabelFormatChange = (event: DropdownChangeParams): void => {
    setLabelFormat(event.value)
  }

  useEffect(() => {
    if (userDetail && constants) {
      const res = userDetail.results.userDetail

      setFirstname(res.firstname)
      setLastname(res.lastname)
      setEmail(res.email)
      setBillingEmail(res.billingEmail)
      setBillingAdress(res.billingAddress || "")
      setLabelFormat(res.labelFormat || "A6")

      if (res.loadingDefaultAddress?.country) {
        setLoadingDefaultAddress(res.loadingDefaultAddress)
      }

      if (res.phone) {
        const { prefix, number } = splitNumber(constants.results.countries, res.phone)
        setPhonePrefix(prefix)
        setPhone(number)
      }

      if (res.mobile) {
        const { prefix, number } = splitNumber(constants.results.countries, res.mobile)
        setMobilePrefix(prefix)
        setMobile(number)
      }
    }
  }, [userDetail, constants])

  const [errors, setErrors] = useState<FormErrors<UpdateUserPayload>>()

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    const payload: UpdateUserPayload = {
      firstname,
      lastname,
      email,
      billingEmail,
      phone: phone ? `${phonePrefix}${phone}` : "",
      mobile: mobile ? `${mobilePrefix}${mobile}` : "",
      canManageUsers: user?.canManageUsers || false, // user can't change itself
      billingAddress,
      loadingDefaultAddress,
      labelFormat,
    }

    const [isValid, errors] = validateUser(payload, isAdmin)
    setErrors(errors)

    if (isValid) {
      await updateUser.mutateAsync(payload)
        .then((response) => {
          setUser(response.results.userDetail)
          savedMessageRef.current?.show({ severity: "success", summary: "Úspěšně uloženo" })
        })
        .catch(({ response }) => {
          let { data }: { data: GetUserResponseData } = response
          let respErrors: FormErrors<UpdateUserPayload> = {}

          data.errors.forEach((error) => {
            if (typeof error.property === "string") {
              respErrors[error.property as keyof UpdateUserPayload] = error.message
            }
          })

          setErrors(respErrors)
          scrollToFirstInvalidElement()
        })
    }
  }

  const tAddressItem = (option: Address): ReactNode => formatAddress(option)

  const tAddressValue = (option: Address, { placeholder }: DropdownProps): ReactNode => {
    if (option) {
      return formatAddress(option)
    }

    return <div className={styles.placeholder}>{placeholder}</div>
  }

  const savedMessageRef = useRef<Toast>(null)

  const [isAddressBookVisible, setIsAddressBookVisible] = useState<boolean>(false)
  const onAddressBookHide = () => setIsAddressBookVisible(false)
  const onAddressBookShow = () => setIsAddressBookVisible(true)
  const onAddressBookSelect = (address: AddressSimple) => {
    onAddressBookHide()
    setLoadingDefaultAddress(Object.assign({}, loadingDefaultAddress, {
      city: address.city,
      country: address.country,
      name: address.name,
      person: address.person,
      street: address.street,
      streetDetails: address.streetDetails,
      zip: address.zip,
      phone: address.phone,
      mobile: address.mobile,
      email: address.email,
    }))
  }

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setErrors(Object.assign({}, errors, obj))
  }

  const isAdmin: boolean = userDetail?.results.userDetail.isAdmin || false

  const handleChangePasswordClick = () => navigate("/profile/change-password")

  return (
    <form className={"col-12"} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>
        <div className={"block-title block-title_blue px-3 py-2 border-round-top flex align-items-center"}>
          {user?.isActive
            ? <i className={"pi pi-check-circle text-green-700"} title={"registrace potvrzena"}></i>
            : <i className={"pi pi-exclamation-circle text-red-700"} title={"registrace čeká na potvrzení"}></i>
          }
          <span className={"ml-3"}>Profil</span>
          <Button label={"Změna hesla"} className={"p-button-link p-button-sm w-auto ml-auto text-color"}
            icon={"pi pi-lock"} onClick={handleChangePasswordClick} type={"button"} />
        </div>

        <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={firstname} onChange={handleFirstnameChange}
                  className={classNames({ "p-invalid": !!errors?.firstname })} />
                <label className={commonStyles.labelRequired}>Jméno:</label>
              </span>
              <FieldError error={errors?.firstname} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={lastname} onChange={handleLastnameChange}
                  className={classNames({ "p-invalid": !!errors?.lastname })} />
                <label className={commonStyles.labelRequired}>Příjmení:</label>
              </span>
              <FieldError error={errors?.lastname} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={email} onChange={handleEmailChange}
                  className={classNames({ "p-invalid": !!errors?.email })}
                  aria-autocomplete={"none"} />
                <label className={commonStyles.labelRequired}>E-mail:</label>
              </span>
              <FieldError error={errors?.email} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={billingEmail} onChange={handleBillingEmailChange}
                  className={classNames({ "p-invalid": !!errors?.billingEmail })}
                  aria-autocomplete={"none"} />
                <label className={isAdmin ? commonStyles.labelRequired : ""}>E-mail pro fakturaci:</label>
              </span>
              <FieldError error={errors?.billingEmail} />
              {!isAdmin && (
                <small className={commonStyles.fieldHelp}>Tato adresa bude použita pro zasílání faktur, jinak se použije fakturační e-mail zadaný u admina společnosti.</small>
              )}
              <small className={commonStyles.fieldHelp}> Více e-mailů oddělte středníkem.</small>
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <PhoneInput placeholder={"Mobil:"} prefix={mobilePrefix}
                value={mobile} onChange={handleMobileChange}
                isRequired={!phone} error={errors?.mobile} />
            </div>

            <div className={"field col-12 md:col-4"}>
              <PhoneInput placeholder={"Telefon:"} prefix={phonePrefix}
                value={phone} onChange={handlePhoneChange}
                isRequired={!mobile} error={errors?.phone} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-8"}>
              <span className="p-float-label">
                <Dropdown options={addresses?.results.addressList || []}
                  optionValue={"uuid"} optionLabel={"name"}
                  itemTemplate={tAddressItem} valueTemplate={tAddressValue}
                  value={billingAddress} onChange={handleBillingAddressChange}
                  className={classNames({ "p-invalid": !!errors?.billingAddress })}
                  placeholder={""} />
                <label className={commonStyles.labelRequired}>Fakturační adresa:</label>
              </span>
              <FieldError error={errors?.billingAddress} />
            </div>
          </div>

          <div className={"grid"}>
            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <Dropdown options={labelFormatOptions} optionValue={"value"} optionLabel={"label"}
                  value={labelFormat} onChange={handleLabelFormatChange}
                  className={classNames({ "p-invalid": !!errors?.labelFormat })} />
                <label className={commonStyles.labelRequired}>Formát štítků:</label>
              </span>
              <FieldError error={errors?.labelFormat} />
            </div>
          </div>
        </div>
      </div>

      <div className={"block-title block-title_blue px-3 py-2 border-round-top flex align-items-center"}>
        Výchozí adresa nakládky

        {loadingDefaultAddress && (
          <Button icon={"pi pi-times"} type={"button"}
            className={"p-button-text p-button-icon-only p-button-danger ml-auto"}
            onClick={handleRemoveLoadingDefaultAddress}
          />
        )}
      </div>

      <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6 p-fluid"}>
        <div className={"grid"}>
          <div className={"field col-12 md:col-4"}>
            <span className="p-float-label">
              <Dropdown options={addresses?.results.addressList || []}
                optionValue={"uuid"} optionLabel={"name"}
                itemTemplate={tAddressItem} valueTemplate={tAddressValue}
                value={null} onChange={handleLoadingDefaultAddressChange}
                className={classNames({ "p-invalid": !!errors?.loadingDefaultAddress })}
                placeholder={""} />
              <label>Vyberte jednu ze svých poboček...</label>
            </span>
            <FieldError error={errors?.loadingDefaultAddress} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <Button label={"...nebo vyberte z adresáře"} type={"button"}
              icon={"pi pi-book"} className={"p-button-secondary p-button-sm"}
              onClick={onAddressBookShow} />
          </div>
        </div>

        {loadingDefaultAddress && (
          <div className={"grid mt-3"}>
            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={loadingDefaultAddress.name} />
                <label>Název odesílatele:</label>
              </span>
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={loadingDefaultAddress.street} />
                <label>Ulice a číslo domu:</label>
              </span>
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={loadingDefaultAddress.streetDetails || ""} />
                <label>Upřesnění:</label>
              </span>
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={loadingDefaultAddress.zip} />
                <label>PSČ:</label>
              </span>
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className={"p-float-label"}>
                <InputText value={loadingDefaultAddress.city} />
                <label>Obec:</label>
              </span>
            </div>

            <div className={"field col-12 md:col-4"}>
              <span className="p-float-label">
                <Dropdown options={constants?.results.countries || []}
                  value={loadingDefaultAddress.country}
                  itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                <label>Stát:</label>
              </span>
            </div>
          </div>
        )}
      </div>

      <div className={"text-right"}>
        <Button type={"submit"} label={"Aktualizovat"}
          className={"p-button p-button-sm p-button-success"}
          icon={"pi pi-save"}
          disabled={updateUser.isLoading} />
      </div>

      <Toast ref={savedMessageRef} />

      <AddressBook isVisible={isAddressBookVisible} onHide={onAddressBookHide}
        onSelect={onAddressBookSelect} />
    </form>
  )
}
